import React from 'react';
import { useTranslation } from 'react-i18next';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import MetricCard from './MetricCard';
import CustomIcon from '../../../../components/CustomIcon';
import HealthScoreRing from './HealthScoreRing';
import { useBudget } from '../../context/BudgetContext';
import { formatCAD, formatPercent } from '../../utils/formatCurrency';
import { calculateHealthScore } from '../../utils/financialHealth';

// Stats Canada: Canadian household savings rate ~6-8% (2023-2025 average)
const CANADA_AVG_SAVINGS_RATE = 8;
// CMHC: 30% housing cost benchmark
const CANADA_HOUSING_BENCHMARK = 30;

export default function DashboardOverview() {
  const { t } = useTranslation();
  const { state, derived } = useBudget();

  const healthScore = calculateHealthScore({
    savingsRate: derived.savingsRate,
    housingRatio: derived.housingRatio,
    debtToIncomeRatio: derived.debtToIncomeRatio,
    emergencyFundMonths: derived.currentEmergencyMonths,
    incomeSourceCount: state.incomeSources.length,
  });

  const netColor = derived.monthlyNet >= 0 ? '#2E7D32' : '#C62828';

  // Build benchmark strings
  const savingsBenchmark = derived.savingsRate > CANADA_AVG_SAVINGS_RATE
    ? t('budgetPlanner.dashboard.aboveAvg', 'Promedio Canadá: {{avg}}% — Estás por encima', { avg: CANADA_AVG_SAVINGS_RATE })
    : t('budgetPlanner.dashboard.belowAvg', 'Promedio Canadá: {{avg}}%', { avg: CANADA_AVG_SAVINGS_RATE });

  const housingBenchmark = derived.housingRatio > 0
    ? t('budgetPlanner.dashboard.housingBench', 'Recomendado: <{{pct}}%', { pct: CANADA_HOUSING_BENCHMARK })
    : undefined;

  return (
    <>
    <Grid container spacing={2}>
      <Grid item xs={6} md={3}>
        <MetricCard
          icon={<CustomIcon name="wallet" />}
          label={t('budgetPlanner.dashboard.income', 'Ingresos')}
          value={formatCAD(derived.monthlyIncome)}
          subtitle={`${state.incomeSources.length} fuente(s) de ingreso`}
          benchmark={`${formatCAD(derived.monthlyIncome * 12)}/${t('budgetPlanner.common.year', 'año')}`}
          color="#1E3A5F"
          bgcolor="#F7F9FC"
        />
      </Grid>
      <Grid item xs={6} md={3}>
        <MetricCard
          icon={<CustomIcon name="investments" />}
          label={t('budgetPlanner.dashboard.expenses', 'Gastos')}
          value={formatCAD(derived.monthlyTotalExpenses)}
          subtitle={`${derived.monthlyIncome > 0 ? Math.round((derived.monthlyTotalExpenses / derived.monthlyIncome) * 100) : 0}% de tus ingresos`}
          benchmark={housingBenchmark}
          color="#7D5A00"
          bgcolor="#FFF8E1"
        />
      </Grid>
      <Grid item xs={6} md={3}>
        <MetricCard
          icon={<CustomIcon name="piggyBank" />}
          label={t('budgetPlanner.dashboard.net', 'Disponible')}
          value={formatCAD(derived.monthlyNet)}
          subtitle={`${formatPercent(derived.savingsRate)} ${t('budgetPlanner.dashboard.savingsRate', 'tasa de ahorro')}`}
          benchmark={derived.monthlyNet > 0 ? 'Puedes ahorrar' : 'Déficit mensual'}
          color={netColor}
          bgcolor={derived.monthlyNet >= 0 ? '#E8F5E9' : '#FFEBEE'}
          dominant
        />
      </Grid>
      <Grid item xs={6} md={3}>
        <Card
          elevation={1}
          sx={{
            bgcolor: '#F7F9FC',
            borderTop: '3px solid #1E3A5F',
            height: '100%',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            '&:hover': { transform: 'translateY(-2px)', boxShadow: 4 },
          }}
        >
          <CardContent sx={{ textAlign: 'center', py: { xs: 1.5, md: 2 }, px: { xs: 1, md: 2 } }}>
            <HealthScoreRing score={healthScore} t={t} />
            <Typography
              variant="body2"
              fontWeight={500}
              color="text.primary"
              sx={{ mt: 0.5, fontSize: { xs: '0.75rem', md: '0.875rem' } }}
            >
              {t('budgetPlanner.dashboard.health', 'Salud financiera')}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
    <Typography
      sx={{ fontSize: { xs: 12, md: 11 }, color: '#9CA3AF', textAlign: 'center', mt: 0.5 }}
    >
      {t('budgetPlanner.dashboard.healthContext', 'Basado en tu tasa de ahorro, gastos de vivienda, deudas y fondo de emergencia.')}
    </Typography>
    </>
  );
}
